// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { Dispatch } from 'redux';
import { getJson, postJson } from 'Utils/http';
import { API_URL } from 'Config';
import { RootState } from 'Store';
import { getOrders } from 'Features/order/orderSelectors';
import { UpdateImageryRequestSuccessAction } from 'Features/project/projectActions';
import { UpdateProjectListImageryRequestSuccessAction } from 'Features/projectList/projectListActions';
import { CreateImageryRequestFailedAction, CreateImageryRequestStartAction, CreateImageryRequestSuccessAction, GetImageryCoverageFailedAction, GetImageryCoverageStartAction, GetImageryCoverageSuccessAction, GetImageryProvidersFailedAction, GetImageryProvidersStartAction, GetImageryProvidersSuccessAction } from './imageryActions';

export const GetImageryProvidersThunk = () => async (
  dispatch: Dispatch,
  getState: () => RootState,
) => {
  const url = `${API_URL}/imageryProviders`;
  const { token } = getState().auth;

  dispatch(GetImageryProvidersStartAction());

  const { success, message, data } = await getJson<IImageryProvidersResponse>(url, token);
  if (!success) {
    dispatch(GetImageryProvidersFailedAction());
    return;
  }

  dispatch(GetImageryProvidersSuccessAction(data.imageryProviders));
};

export const GetNearmapImageryCoverageThunk = (points: number[], coordinates: [number, number][]) => async (
  dispatch: Dispatch,
  getState: () => RootState,
) => {
  const { token } = getState().auth;
  const url = `${API_URL}/imageryProvider/nearmapCoverage`;
  dispatch(GetImageryCoverageStartAction());
  const coordinatesString = coordinates.map((coord) => coord.join(',')).join(',');
  const { projectId } = getState().project;
  const orders = getOrders(getState());
  const orderId = orders?.activeOrder?._id;
  const data = { points, polygon: coordinatesString, projectId, orderId };

  const response = await postJson<INearmapCoverageResponse>(url, data, token);

  if (!response.success || !response.data.coverage) {
    const { org } = getState().account;
    const { project } = getState().project;
    const { companyName, imageryProviders } = org;
    const { name } = project;
    const { acreage } = orders.activeOrder;

    const providerTier = getState().imagery.imageryProviders.filter((iP) => iP._id === imageryProviders[0]);

    let providerTierName = 'none';

    if (providerTier.length) {
      providerTierName = providerTier[0].name;
    }

    window?.pendo?.track('Nearmap Imagery Unavailable', {
      companyName,
      projectId,
      projectName: name,
      kmlSize: acreage?.toFixed(2),
      nearmapTier: providerTierName,
    });
  }

  if (!response.success) {
    dispatch(GetImageryCoverageFailedAction());
    return;
  }
  dispatch(GetImageryCoverageSuccessAction(response.data.coverage));
};

export const CreateImageryRequestThunk = (status?: string) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    const { token } = getState().auth;
    const { projectId } = getState().project;
    const { imageryProcessing, nearmapCoverage, horizontalEpsg, verticalEpsg } = getState().imagery;

    const data = {
      ownerProject: projectId,
      processImmediately: imageryProcessing,
      status: status || (nearmapCoverage ? 'covered' : 'uncovered'),
      ...(nearmapCoverage ? { provider: 'Nearmap' } : {}),
      ...(horizontalEpsg ? { horizontalEpsg: Number(horizontalEpsg) } : {}),
      ...(verticalEpsg ? { verticalEpsg: Number(verticalEpsg) } : {}),
    };

    const url = `${API_URL}/imageryRequest`;

    dispatch(CreateImageryRequestStartAction());

    const result = await postJson<{imageryRequest: IImageryRequest}>(url, data, token);
    if (result.success) {
      dispatch(CreateImageryRequestSuccessAction());
      dispatch(UpdateImageryRequestSuccessAction(result.data?.imageryRequest));
      dispatch(UpdateProjectListImageryRequestSuccessAction(result.data?.imageryRequest));
    } else {
      dispatch(CreateImageryRequestFailedAction());
    }

    return result;
  };
